import React, { useState, useEffect, useCallback } from "react";
import PaymentMethod from "../components/PaymentMethod";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ErrorTransactionNotFound from "../components/ErrorTransactionNotFound";
import Notification from "../components/Notification";
import OvoPhoneModal from "../components/OvoPhoneModal";
import { requestToken } from "../../config/firebaseConfig";
import { setInquiryDetails } from "../../redux/slices/inquirySlice";
import { setNotification } from "../../redux/slices/notificationSlice";
import { updateCustomerDetail } from "../../redux/slices/customerSlice";
import { getPaymentLinkHeaders } from "../../utils/paymentLinkHeaders";
import { getMethodSelectionExpiry } from "../../utils/paymentLinkExpiration";
import { PaymentLinkErrorCode } from "../../utils/paymentLinkErrorHandler";
import { getPreInquiryRequirements, resolvePostInquiryNavigation } from "../utils/ewalletFlowRouter";

const BASE_URL = process.env.REACT_APP_API_URL;

const SecondForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { reffno } = useParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [showOvoPhoneModal, setShowOvoPhoneModal] = useState(false);
  const [pendingOvoMethodCode, setPendingOvoMethodCode] = useState(null);

  // Ambil data dari Redux store
  const paymentData = useSelector((state) => state.payment.data);
  const customerData = useSelector((state) => state.customer);
  const errorCode = useSelector((state) => state.payment.errorCode);
  const paymentError = useSelector((state) => state.payment.error);
  const notification = useSelector((state) => state.notification);

  // Fetch token FCM saat komponen dimount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await requestToken();
        setFcmToken(token);
      } catch (err) {
        console.error("Gagal mendapatkan FCM Token:", err.message);
      }
    };

    fetchToken();
  }, []);

  // Fetch payment methods dari API (GET request)
  useEffect(() => {
    if (!paymentData) return;
    console.log("Fetching payment methods for paymentData:", paymentData);

    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${BASE_URL}/api/payment-link/available-payment-method`,
          {
            method: "POST", // Ubah dari GET ke POST
            headers: getPaymentLinkHeaders(),
            body: JSON.stringify({
              payment_link_id: paymentData.id, // Kirim ID di body
              reff_no: paymentData.reff_no, // Required by middleware
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch payment methods");
        }

        const data = await response.json();
        setPaymentMethods(
          data.data.map((method) => ({
            code: method.code.toUpperCase(),
            name: method.name,
            desc: method.desc,
            group: method.group || null,
          }))
        );
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setError("Failed to load payment methods");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [paymentData]);

  const proceedWithInquiry = useCallback(async (methodCode, overridePhone) => {
    if (!paymentData) return;

    const effectiveCustomerData = overridePhone
      ? { ...customerData, phone: overridePhone }
      : customerData;

    setIsProcessing(true);
    try {
      const response = await fetch(`${BASE_URL}/api/payment-link/proceed-inquiry`, {
        method: "POST",
        headers: getPaymentLinkHeaders(),
        body: JSON.stringify({
          reff_no: paymentData.reff_no,
          payment_method_code: methodCode,
          customer_data: JSON.stringify(effectiveCustomerData),
          fcm_token: fcmToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.log("error result: ", result);
        throw new Error(result.message || "Payment processing failed");
      }

      console.log("Inquiry success:", result);

      dispatch(
        setInquiryDetails({
          amount: paymentData.total_amount,
          orderId: result.data.paymentLinkHistory.reff_no,
          merchantName: result.data.paymentLink.account.merchant.name,
          expired_at: result.data.paymentLinkHistory.expired_at,
          selected_method: result.data.paymentLinkHistory.payment_method_name,
          paymentMethod: result.data.paymentMethod,
          paymentLink: result.data.paymentLink,
          paymentLinkHistory: result.data.paymentLinkHistory,
        })
      );

      const nav = resolvePostInquiryNavigation(result, paymentData, effectiveCustomerData);
      navigate(nav.route, { replace: true, state: nav.state });
    } catch (err) {
      console.error("Error processing payment:", err);
      dispatch(setNotification({
        type: "error",
        title: "Gagal Memproses Pembayaran",
        message: err.message || "Terjadi kesalahan saat memproses pembayaran. Silakan coba lagi.",
      }));
    } finally {
      setIsProcessing(false);
    }
  }, [paymentData, customerData, fcmToken, dispatch, navigate]);

  const handleSelectPaymentMethod = useCallback((methodCode) => {
    const { requiresPhone } = getPreInquiryRequirements(methodCode);
    if (requiresPhone) {
      setPendingOvoMethodCode(methodCode);
      setShowOvoPhoneModal(true);
      return;
    }
    proceedWithInquiry(methodCode);
  }, [proceedWithInquiry]);

  const handleOvoPhoneSubmit = useCallback((phone) => {
    dispatch(updateCustomerDetail({ phone }));
    setShowOvoPhoneModal(false);
    if (pendingOvoMethodCode) {
      proceedWithInquiry(pendingOvoMethodCode, phone);
      setPendingOvoMethodCode(null);
    }
  }, [pendingOvoMethodCode, dispatch]);

  // Redirect to /b2b/:reffno if paymentData not found (e.g., after refresh)
  useEffect(() => {
    if (!paymentData && reffno) {
      navigate(`/b2b/${reffno}`, { replace: true });
    }
  }, [paymentData, reffno, navigate]);

  if (!paymentData) {
    return null; // Will redirect via useEffect
    // return <ErrorTransactionNotFound errorCode={errorCode} message={paymentError} />;
  }

  // Calculate countdown expiry: default 5 minutes, or PaymentLink expired_at if sooner
  const countdownExpiry = getMethodSelectionExpiry(paymentData?.expired_at, 5);

  if (process.env.REACT_APP_DEBUG) {
    console.log('SecondForm Countdown Expiry:', {
      paymentLinkExpiredAt: paymentData?.expired_at,
      fiveMinutesFromNow: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      countdownExpiry,
      source: countdownExpiry === paymentData?.expired_at ? 'PaymentLink expired_at' : '5 minutes default'
    });
  }

  return (
    <div>
      <Notification
        message={notification.message}
        type={notification.type}
        title={notification.title}
      />
      {loading && <p className="text-center text-gray-500">Loading payment methods...</p>}
      {error && <p className="text-center text-red-500">{error}</p>}
      {isProcessing && <p className="text-center text-blue-500">Processing payment...</p>}
      {!loading && !error && (
        <PaymentMethod
          orderId={paymentData.reff_no}
          paymentTo={paymentData.account.merchant.name}
          amount={paymentData.total_amount}
          paymentMethodOptions={paymentMethods}
          customerData={customerData}
          onSelectMethod={handleSelectPaymentMethod}
          expiredAt={countdownExpiry}
        />
      )}
      <OvoPhoneModal
        isOpen={showOvoPhoneModal}
        onClose={() => {
          setShowOvoPhoneModal(false);
          setPendingOvoMethodCode(null);
        }}
        onSubmit={handleOvoPhoneSubmit}
        isProcessing={isProcessing}
        initialPhone={customerData.phone}
      />
    </div>
  );
};

export default SecondForm;
